import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faTrash as faTrashSolid } from '@fortawesome/pro-solid-svg-icons';
import {
    IUserLocationChild,
    IUserResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { MemoizedTable } from 'components/data_table/Table';
import { TableColumn } from 'components/data_table/types';
import { ActionMenuTableCell } from 'components/menu/ActionMenuTableCell';
import { usePermissions } from 'contexts/PermissionsContext';
import { StyledCell } from 'pages/Settings/Users/shared';
import { LocationUserRoleCell } from 'pages/Settings/Users/table_cells/LocationUserRoleCell';
import { useMemo } from 'react';
import { DebugValue } from 'shared/debug/DebugValue';
import { useThemeValues } from 'styles/theme';

export type LocationsForUserTableProps = Readonly<{
    selectedUserInfo: IUserResponse;
    onDeleteLocationForUserClick: (userLocationChild: IUserLocationChild) => void;
    onChange: () => void;
}>;

export function LocationsForUserTable({
    selectedUserInfo,
    onDeleteLocationForUserClick,
    onChange,
}: LocationsForUserTableProps) {
    const { secondaryText, deleteButton } = useThemeValues();
    const { canEditUserLocationAssignment } = usePermissions();

    const columns = useMemo<TableColumn<IUserLocationChild>[]>(
        () => [
            {
                id: 'LocationName',
                header: () => 'Location Name',
                value: ({ rowData }) => (
                    <StyledCell>
                        {rowData.LocationName}
                        <DebugValue value={rowData} description="LocationToUser" />
                    </StyledCell>
                ),
                primary: true,
                sortOn: 'LocationName',
            },
            {
                id: 'LocationRole',
                header: () => 'Location Role',
                value: ({ rowData }) => (
                    <LocationUserRoleCell
                        rowData={selectedUserInfo}
                        locationId={rowData.LocationId}
                        onChange={onChange}
                        disabled={
                            !canEditUserLocationAssignment(
                                rowData.LocationCorporationId,
                                rowData.LocationId
                            )
                        }
                    />
                ),
                primary: true,
                sortOn: 'LocationUserRole',
            },
            {
                id: 'actions',
                header: () => 'Actions',
                value: ({ rowData }) => {
                    const canEdit = canEditUserLocationAssignment(
                        rowData.LocationCorporationId,
                        rowData.LocationId
                    );
                    return (
                        <ActionMenuTableCell>
                            <HoverIconButton
                                className="flex-row no-wrap"
                                icon={faTrash}
                                hoverIcon={faTrashSolid}
                                color={secondaryText}
                                hoverColor={deleteButton}
                                onClick={() => onDeleteLocationForUserClick(rowData)}
                                title={
                                    canEdit
                                        ? 'Delete Location assignment'
                                        : 'Insufficient permissions to delete'
                                }
                                disabled={!canEdit}
                            />
                        </ActionMenuTableCell>
                    );
                },
            },
        ],
        [
            canEditUserLocationAssignment,
            deleteButton,
            onChange,
            onDeleteLocationForUserClick,
            secondaryText,
            selectedUserInfo,
        ]
    );

    return (
        <>
            {selectedUserInfo.Locations.length > 0 ? (
                <MemoizedTable
                    tableStyle="data"
                    data={selectedUserInfo.Locations}
                    rowKey={({ LocationId }: IUserLocationChild) => LocationId}
                    columns={columns}
                />
            ) : (
                <span>No locations have been assigned to this user yet.</span>
            )}
        </>
    );
}
